import {ThunkActionType} from 'store/types';
import {v4 as uuidv4} from 'uuid';
import {push} from 'connected-react-router';

import request from 'api/requests/request';
import {deleteImage} from 'utils';
import {InternalError} from 'utils/error';

import {PatchProps} from 'api/types';
import {OPERATE_TYPES} from 'api/constants';

import {
  convertBitToPageDTO,
  convertWisbitToLessonDTO,
  convertBitComponentToComponentDTO,
  convertLessonDTOToWisbit,
} from 'api/converters/wisbitConverters';
import {LessonDTO} from 'api/typesDTO/wisbitDTO';

import {ROUTE, MODE} from 'containers/App/constants';
import {Template} from 'containers/ChooseTemplatePage/types';

import {Options, Bit, Component} from '../types';
import {selectDefaultValue} from '../utils';
import {BIT_FIELDS, FIELDS, COMPONENT_TYPES} from '../constants';

import {
  saveWisbitRequest,
  saveWisbitFailure,
  saveWisbitSuccess,
  loadWisbitRequest,
  loadWisbitSuccess,
  loadWisbitFailure,
  initiateWisbit,
  publishWisbitRequest,
  publishWisbitFailure,
  publishWisbitSuccess,
  deleteWisbitRequest,
  deleteWisbitSuccess,
  deleteWisbitFailure,
} from './actions';

async function fetchWisbit(id: string): Promise<LessonDTO> {
  return request(`/lesson/${id}?fields=pages,tags`);
}

export const loadWisbit =
  (id: string): ThunkActionType =>
  async dispatch => {
    dispatch(loadWisbitRequest(id));

    try {
      const response = await fetchWisbit(id);
      const wisbit = await convertLessonDTOToWisbit(response);
      dispatch(loadWisbitSuccess(wisbit));
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    } catch (error: any) {
      dispatch(loadWisbitFailure(error));
    }
  };

export const saveWisbit =
  (options?: Options): ThunkActionType =>
  async (dispatch, getState) => {
    dispatch(saveWisbitRequest());

    const {wisbit} = getState();

    const {id, bits} = wisbit;

    try {
      let deletingImageId: string | undefined = undefined;

      if (id) {
        if (options) {
          const {
            op,
            field,
            bitField,
            tagId,
            tagIndex,
            thumbnailId,
            to: {bitIndex = undefined, componentIndex = undefined} = {},
            from: {
              bitIndex: fromBitIndex = undefined,
              componentIndex: fromComponentIndex = undefined,
            } = {},
          } = options;

          const values = [];
          let path = '';
          let from = '';
          switch (field) {
            case FIELDS.TITLE:
            case FIELDS.DESC: {
              path = field;
              values.push(wisbit[field]);
              break;
            }
            case FIELDS.THUMBNAIL: {
              path = 'image';
              values.push({id: thumbnailId});
              // try to delete old thumbnail (our state has been updated by now, need to fetch old image id from api)
              try {
                deletingImageId = (await fetchWisbit(id)).image?.id;
              } catch (e) {
                //ignore
              }
              break;
            }
            case FIELDS.BITS: {
              const PAGES = 'pages';
              const COMPONENTS = '/components';

              let SUBPATH = undefined;
              if (bitField === BIT_FIELDS.TITLE) {
                SUBPATH = '/title';
              } else if (bitField === BIT_FIELDS.DURATION) {
                SUBPATH = '/duration_estimate';
              }

              path = PAGES;
              if (bitIndex !== undefined && componentIndex !== undefined) {
                path = `${path}/${bitIndex}${COMPONENTS}/${componentIndex}`;
              } else if (bitIndex !== undefined) {
                path = `${path}/${bitIndex}${
                  op === OPERATE_TYPES.ADD
                    ? `${COMPONENTS}/-`
                    : SUBPATH
                    ? SUBPATH
                    : ''
                }`;
              } else {
                path = `${path}/-`;
              }

              if (op === OPERATE_TYPES.ADD) {
                if (bitIndex !== undefined) {
                  const bit = bits ? bits[bitIndex] : undefined;
                  const component = bit?.components
                    ? bit?.components[bit?.components.length - 1]
                    : undefined;

                  if (component) {
                    if (
                      component.type === COMPONENT_TYPES.OPEN_QUESTION ||
                      component.type === COMPONENT_TYPES.MULTIPLE_CHOICES
                    ) {
                      const newTextComponent = {
                        id: '',
                        type: COMPONENT_TYPES.TEXT,
                        value: component.value,
                      };

                      const converted = await convertBitComponentToComponentDTO(
                        newTextComponent
                      );
                      values.push(converted);
                    }

                    const converted = await convertBitComponentToComponentDTO(
                      component
                    );
                    values.push(converted);
                  }
                } else {
                  const converted = await convertBitToPageDTO({id: ''});
                  values.push(converted);
                }
              } else if (op === OPERATE_TYPES.REPLACE) {
                if (bitIndex !== undefined && componentIndex !== undefined) {
                  const bit = bits ? bits[bitIndex] : undefined;
                  const component = bit?.components
                    ? bit?.components[componentIndex]
                    : undefined;

                  if (component) {
                    const converted = await convertBitComponentToComponentDTO(
                      component
                    );
                    values.push(converted);

                    // our state has been updated (in-place), so we'll need an api reaquest to determine the previous image id
                    try {
                      const oldData = await convertLessonDTOToWisbit(
                        await fetchWisbit(id)
                      );
                      const oldImageId =
                        oldData.bits?.[bitIndex].components?.[componentIndex]
                          .imageId;
                      if (oldImageId && oldImageId !== converted.image?.id) {
                        deletingImageId = oldImageId;
                      }
                    } catch (e) {
                      // ignore
                    }
                  }
                } else if (bitIndex !== undefined) {
                  const bit = bits ? bits[bitIndex] : undefined;

                  if (bit && bitField) {
                    if (bitField === BIT_FIELDS.TITLE) {
                      values.push(bit.title);
                    } else if (bitField === BIT_FIELDS.DURATION) {
                      values.push(
                        (bit.duration && bit.duration > 0 ? bit.duration : 3) *
                          60
                      );
                    }
                  } else if (bit) {
                    const converted = await convertBitToPageDTO(bit);
                    values.push(converted);
                  }
                }
              } else if (op === OPERATE_TYPES.MOVE) {
                from = `${PAGES}/${fromBitIndex}${
                  fromComponentIndex !== undefined
                    ? `${COMPONENTS}/${fromComponentIndex}`
                    : ''
                }`;
              }

              break;
            }
            case FIELDS.TAGS: {
              path = `tags/${tagIndex}`;
              values.push({id: tagId});
            }
          }

          let response = undefined;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const patchRequest = async (value?: any) => {
            const operate: PatchProps = {
              op,
              path,
              value,
              from,
            };
            const res = await request(`/lesson/${id}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json-patch+json',
              },
              data: [operate],
            });

            return res;
          };

          if (values && values.length > 0) {
            for (const value of values) {
              response = await patchRequest(value);
            }
          } else {
            response = await patchRequest();
          }

          const updatedWisbit = await convertLessonDTOToWisbit(response);

          dispatch(saveWisbitSuccess(updatedWisbit));

          if (deletingImageId) {
            await deleteImage(deletingImageId);
          }
        }
      } else {
        const lesson: LessonDTO = await convertWisbitToLessonDTO(
          dispatch,
          wisbit
        );

        const response = await request('/lesson', {
          method: 'POST',
          data: lesson,
        });

        const newId = response.id;

        if (newId) {
          dispatch(saveWisbitSuccess({id: newId}));

          dispatch(push(`${ROUTE.Wisbit.Index}/${newId}/${MODE.EDIT}`));
        } else {
          dispatch(
            saveWisbitFailure(
              new InternalError('Er is iets misgegaan, probeer het opnieuw.')
            )
          );
        }
      }
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    } catch (error: any) {
      dispatch(saveWisbitFailure(error));
      if (!id) {
        return;
      }
      dispatch(loadWisbit(id));
    }
  };

export const loadWisbitTemplate =
  (templateType: string): ThunkActionType =>
  async (dispatch, getState) => {
    const {
      template: {themes},
    } = getState();

    let bits = undefined;
    if (themes) {
      const {templates} = themes[0];

      const {template} =
        (templates as (Template & {template: {bits: Bit[]}})[]).find(
          ({id}) => id === templateType
        ) || {};

      // Ids should be defined for drag and drop
      bits = template
        ? template.bits.map(({title, components}) => ({
            id: uuidv4(),
            title,
            components: (components as Partial<Component>[]).map(
              ({type, value}) => {
                const selectedDefaultValue = selectDefaultValue(type);
                if (value) {
                  selectedDefaultValue.value = value;
                }
                return {
                  id: uuidv4(),
                  type,
                  ...selectedDefaultValue,
                };
              }
            ),
            duration: 3,
          }))
        : [];
    }

    dispatch(initiateWisbit(bits, templateType, true));
  };

export const publishWisbit =
  (id: string): ThunkActionType =>
  async dispatch => {
    dispatch(publishWisbitRequest());

    try {
      const response = await request(`/lesson/${id}/publish`, {
        method: 'POST',
      });

      console.log(response);

      dispatch(publishWisbitSuccess());
      dispatch(push(ROUTE.Dashboard));
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    } catch (error: any) {
      console.log(error);

      if (error.status === 400) {
        error.setMessage(
          'Voeg inhoud aan je bits toe om de Wisbit te kunnen publiceren'
        );
      }

      dispatch(publishWisbitFailure(error));
    }
  };

export const deleteWisbit =
  (id: string, isPublished: boolean): ThunkActionType =>
  async dispatch => {
    dispatch(deleteWisbitRequest());

    try {
      await request(`/lesson/${id}${isPublished ? '?published=true' : ''}`, {
        method: 'DELETE',
      });

      dispatch(deleteWisbitSuccess());
      dispatch(push(ROUTE.Wisbit.Index));
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    } catch (error: any) {
      dispatch(deleteWisbitFailure(error));
    }
  };
